// Per-trip view: GPS map placeholder + banner/state/event timeline + discrepancy
// markers, plus a minute-by-minute play-through scrubber. Clicking a discrepancy
// row (or its "flagged" badge) opens the DiscrepancyDetail drill-down.

import { useEffect, useMemo, useState } from "react";
import { getTripReport } from "../api.js";
import type { Discrepancy, TripReport } from "../types.js";
import { buildTimeline, buildTrace, minuteOf, timeBounds } from "../timeline.js";
import { MapTrace } from "./MapTrace.js";
import { DiscrepancyDetail } from "./DiscrepancyDetail.js";

interface Props {
  tripId: string;
}

export function TripReportView({ tripId }: Props) {
  const [report, setReport] = useState<TripReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [minute, setMinute] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [selected, setSelected] = useState<Discrepancy | null>(null);

  useEffect(() => {
    let alive = true;
    setReport(null);
    setError(null);
    setMinute(0);
    setPlaying(false);
    getTripReport(tripId)
      .then((r) => alive && setReport(r))
      .catch((e: unknown) => alive && setError(String(e)));
    return () => {
      alive = false;
    };
  }, [tripId]);

  const trace = useMemo(() => (report ? buildTrace(report) : []), [report]);
  const timeline = useMemo(() => (report ? buildTimeline(report) : []), [report]);
  const bounds = useMemo(() => timeBounds(timeline), [timeline]);

  // Play-through clock: advance one minute per second while playing.
  useEffect(() => {
    if (!playing) return;
    const id = window.setInterval(() => {
      setMinute((m) => {
        if (m >= bounds.minuteCount) {
          setPlaying(false);
          return m;
        }
        return m + 1;
      });
    }, 1000);
    return () => window.clearInterval(id);
  }, [playing, bounds.minuteCount]);

  if (error) return <p className="sev error">Failed to load report: {error}</p>;
  if (!report) return <p className="muted">Loading trip report…</p>;

  const cursorTs = bounds.start + minute * 60_000;
  const cursorPoint =
    trace.length > 0
      ? [...trace].reverse().find((p) => p.ts <= cursorTs) ?? trace[0]
      : null;

  return (
    <>
      <div className="panel">
        <strong>{report.trip.id}</strong>{" "}
        <span className="muted">
          {new Date(report.trip.start).toLocaleString()} · {(report.trip.distanceM / 1000).toFixed(1)} km ·{" "}
          {report.trip.discrepancyCount} discrepancies
        </span>
      </div>

      <div className="two-col">
        <div className="panel">
          <h3>GPS trace</h3>
          <MapTrace
            trace={trace}
            discrepancies={report.discrepancies}
            cursor={cursorPoint ? { lat: cursorPoint.lat, lng: cursorPoint.lng } : null}
          />
          <p className="muted" style={{ fontSize: 12 }}>
            Placeholder plot (no tiles). Blue = trace, ringed dots = discrepancies, green = play cursor.
          </p>

          <h3>Play-through</h3>
          <div className="scrubber">
            <button onClick={() => setPlaying((p) => !p)}>{playing ? "Pause" : "Play"}</button>
            <input
              type="range"
              min={0}
              max={bounds.minuteCount}
              value={minute}
              onChange={(e) => setMinute(Number(e.target.value))}
            />
            <span className="muted">
              min {minute}/{bounds.minuteCount}
            </span>
          </div>
        </div>

        <div className="panel">
          <h3>Timeline</h3>
          <ul className="timeline">
            {timeline.map((ev, i) => {
              const dim = ev.ts > cursorTs;
              const isDisc = ev.kind === "discrepancy";
              return (
                <li
                  key={i}
                  className={`${dim ? "dim" : ""} ${isDisc ? "clickable" : ""}`.trim()}
                  onClick={isDisc ? () => setSelected(ev.source as Discrepancy) : undefined}
                >
                  <span className="muted">+{minuteOf(ev.ts, bounds)}m</span>
                  <span className="kind">
                    {isDisc && ev.severity ? (
                      <span className={`sev ${ev.severity}`}>{ev.kind}</span>
                    ) : (
                      ev.kind
                    )}
                  </span>
                  <span>{ev.label}</span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {selected && (
        <DiscrepancyDetail discrepancy={selected} report={report} onClose={() => setSelected(null)} />
      )}
    </>
  );
}
